import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import _ from 'lodash';
import UtilityBar from '../utilityBar/UtilityBar';
import StepNav from '../stepNav/StepNav';
import BreakpointChange from '../../common/mediaQuery/BreakpointChange';
import { breakpointChanged } from '../../store/actions';
import { connect } from 'react-redux';
import { combineClassNames } from 'core/css';
import { CLASSNAME_UPDATING } from '../../constants';
import useLargeBreakpoint from '../../hooks/useLargeBreakpoint';

import BottomNav, {
  BottomNavButtonControls,
  BottomNavPrimaryButtonLink,
  BottomNavSummaryButton,
  BottomNavMoreViews,
  BottomNavPricingSection,
  BottomNavContent,
} from '../../components/bottomNav/BottomNav';
import { Translate } from 'core/translate';
import {
  CONFIG,
  TRIMS,
  OPTIONS,
  INTERIOR,
  SUMMARY,
  WHEELS,
  PACKAGES_AND_OPTIONS,
  MODELS,
} from '../../common/steps';
import useAnimateScroll from '../../hooks/useAnimateScroll';

const MainTemplate = ({
  config,
  modelInfo,
  onBreakpointChange,
  isSelectionRevealed,
  isBodyTypeRevealed,
  pricing,
  matrix,
  disclaimer,
  steps,
  isUpdating,
  savedBuildCount,
  isNotificationOpen,
  setBottomOffset,
  navRendered,
}) => {
  const {
    year,
    title: bodyStyle,
    enableFallbackNavigation,
    hasTransactionId,
  } = modelInfo;
  const bodyTypeDescription = _.get(config, 'bodyType.formattedConfig', '');
  const trimName = _.get(config, 'style.trimName', '');
  const driveType = _.get(config, 'driveType.label', '');
  const { startScroll, cancelAnimation: cancelScroll } = useAnimateScroll();

  const location = useLocation();

  const [displayContainer, setDisplayContainer] = useState(false);

  const [lastKeySet, setLastKeySet] = useState();
  const [initialLoad, setInitialLoad] = useState(true);

  const { active: activeStep, items } = steps;

  const renderNav = activeStep && activeStep.key !== 'summary';
  const isAtLeastLarge = useLargeBreakpoint();

  const navWrapperEl = useRef(null);
  const fallbackNavEl = document.querySelector('.fallback-global-nav');

  const nextStep =
    (activeStep &&
      items &&
      items.find((step) => step.index === activeStep.index + 1)) ||
    {};

  const nextStepLink =
    nextStep.path && nextStep.path.charAt(0) === '/'
      ? nextStep.path.substr(1)
      : nextStep.path;

  // Configure if we should display certain element based on activeStep.
  const navConfig = {
    displaySummaryBtn:
      activeStep &&
      activeStep.key !== TRIMS &&
      activeStep.key !== CONFIG &&
      activeStep.key !== MODELS &&
      activeStep.key !== PACKAGES_AND_OPTIONS &&
      activeStep.key !== OPTIONS,
    displayMoreViews: isSelectionRevealed,
    displayJelly: isSelectionRevealed,
  };

  const shouldStick =
    isAtLeastLarge && activeStep && activeStep.key !== SUMMARY;

  const stickNav = () => {
    const stickyNavContainer = navWrapperEl.current.querySelector(
      '.step-nav-container'
    );

    if (stickyNavContainer && shouldStick) {
      if (stickyNavContainer.getBoundingClientRect().top <= 0) {
        stickyNavContainer.classList.add('sticky-nav');
      } else {
        stickyNavContainer.classList.remove('sticky-nav');
      }
    }
  };

  useEffect(() => {
    const currentStep = activeStep && activeStep.key;

    let showNav = false;
    if (currentStep) {
      showNav =
        (isBodyTypeRevealed &&
          (currentStep === CONFIG || currentStep === MODELS)) ||
        isSelectionRevealed;
    }
    if (showNav) {
      setDisplayContainer(true);
    } else {
      setDisplayContainer(false);
    }
  }, [isBodyTypeRevealed, isSelectionRevealed, location]);

  useEffect(() => {
    const removeSticky = () => {
      const stickyElWrapper = navWrapperEl.current.querySelectorAll(
        '.sticky-nav'
      );
      if (stickyElWrapper.length > 0) {
        stickyElWrapper.forEach((el) => {
          if (el.classList.contains('step-nav-container') && shouldStick) {
            return;
          }
          el.classList.remove('sticky-nav');
        });
      }
      stickNav();
    };
    removeSticky();
  }, [steps]);

  useEffect(() => {
    if (
      activeStep &&
      lastKeySet !== activeStep.key &&
      navRendered &&
      !isNotificationOpen
    ) {
      const timeout = initialLoad ? 1500 : 0;
      let anchoringTimeout = null;
      let currentAnchor = null;
      const utilityAnchor = enableFallbackNavigation
        ? fallbackNavEl
        : navWrapperEl.current;
      const stepNavAnchor = navWrapperEl.current.querySelector(
        '.step-nav-container'
      );
      if (
        isAtLeastLarge ||
        activeStep.key === CONFIG ||
        activeStep.key === TRIMS
      ) {
        currentAnchor = utilityAnchor;
      } else {
        currentAnchor = stepNavAnchor;
      }
      const startScrollAnimation = () => {
        cancelScroll();
        startScroll(currentAnchor);
      };

      anchoringTimeout = setTimeout(() => {
        startScrollAnimation();
      }, timeout);

      const clearAnchor = () => {
        clearTimeout(anchoringTimeout);
      };

      if (timeout) {
        window.addEventListener('scroll', clearAnchor);

        const listenerTimeout = setTimeout(() => {
          window.removeEventListener('scroll', clearAnchor);
          clearTimeout(listenerTimeout);
        }, timeout);
      }

      setLastKeySet(activeStep.key);
      setInitialLoad(false);
    }
  }, [activeStep, navRendered, isNotificationOpen, isAtLeastLarge]);

  useEffect(() => {
    window.addEventListener('scroll', stickNav);
    window.addEventListener('resize', stickNav);
    return () => {
      window.removeEventListener('scroll', stickNav);
      window.removeEventListener('resize', stickNav);
    };
  }, [steps, isAtLeastLarge]);

  useEffect(() => {
    window?.heap.track('BYO-Page-Load', {load: 'success'});
  },[])

  return (
    <>
      <div ref={navWrapperEl}>
        <div className="nav-wrapper">
          <div className="utility-wrapper">
            {(!isAtLeastLarge && enableFallbackNavigation) ||
            !enableFallbackNavigation ? (
              <UtilityBar
                year={year}
                bodyStyle={bodyStyle}
                driveType={driveType}
                config={bodyTypeDescription}
                trim={trimName}
                isSelectionRevealed={isSelectionRevealed}
                isBodyTypeRevealed={isBodyTypeRevealed}
                savedBuildCount={savedBuildCount}
              />
            ) : null}
            <StepNav rawSteps={steps} />
          </div>
        </div>
        <div id="contentView" className="step-container">
          <Outlet />
        </div>
        {renderNav && displayContainer && (
          <BottomNav
            displayAccessories={
              !pricing.accessoriesTotal
                ? 'bottom-nav-container--no-accessories'
                : ''
            }
            setBottomOffset={setBottomOffset}>
            <BottomNavContent>
              <BottomNavMoreViews
                analyticsKey="bottom nav"
                classIdentifier="main-template"
                location={location}
                displayJellyImg={navConfig.displayJelly}
                displayMoreViewsLbl={navConfig.displayMoreViews}
                moreViewsLbl={
                  <Translate value="more.views.v3" /> || 'More Views'
                }
                className={isUpdating && CLASSNAME_UPDATING}
                displayExterior={activeStep && activeStep.key !== INTERIOR}
                angle={
                  activeStep && activeStep.key === WHEELS ? 'profile' : null
                }
              />
              <BottomNavPricingSection
                config={config}
                matrix={matrix}
                pricing={pricing}
                disclaimer={disclaimer}
                isSelectionRevealed={isSelectionRevealed}
                className={combineClassNames([
                  'bottom-nav--slideup',
                  isUpdating && CLASSNAME_UPDATING,
                ])}
              />
            </BottomNavContent>
            <BottomNavButtonControls
              className={`${isUpdating && CLASSNAME_UPDATING}`}>
              {navConfig.displaySummaryBtn && !hasTransactionId && (
                <BottomNavSummaryButton
                  className="bottom-nav--fade-in"
                  analyticsKey="bottom nav"
                />
              )}
              <BottomNavPrimaryButtonLink
                targetUrl={nextStepLink}
                label={
                  <span>
                    <Translate value="byo.page.general.next.step.v3" />
                    <span className="bottom-nav__next-lbl">
                      {`: ${nextStep.label}`}
                    </span>
                  </span>
                }
                className="bottom-nav--fade-in"
                analyticsKey="bottom nav"
              />
            </BottomNavButtonControls>
          </BottomNav>
        )}
        <BreakpointChange onChange={onBreakpointChange} />
      </div>
    </>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    config,
    breakpoint,
    isSelectionRevealed,
    isBodyTypeRevealed,
    matrix,
    disclaimers,
    isUpdating,
    savedBuildCount,
  } = state;

  const disclaimer = disclaimers.simple;
  return {
    config,
    breakpoint,
    isSelectionRevealed,
    isBodyTypeRevealed,
    matrix,
    disclaimer,
    isUpdating,
    savedBuildCount,
    ...ownProps,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onBreakpointChange: (breakpoint) => {
      dispatch(breakpointChanged(breakpoint));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MainTemplate);
